/**
 * BalticRoots — Full translations for EN / DE / FR / LV
 * Usage: import { useT } from '@/i18n/useT';  then  const t = useT();  t('hero.title')
 */

export const translations = {

  // ─────────────────────────── ENGLISH ───────────────────────────
  EN: {
    // Announcement bar
    announcement: "Free shipping over €75  ·  Amber from the Baltic Sea  ·  Authentic Latvian craft",

    // Nav
    nav: {
      shop: "Shop",
      ourStory: "Our Story",
      latvia: "Latvia",
      journal: "Journal",
    },

    // Cart
    cart: {
      yourBag: "Your Bag",
      empty: "Your bag is quiet for now.",
      subtotal: "Subtotal",
      checkout: "Checkout",
      taxNote: "Taxes and shipping calculated at checkout",
      remove: "Remove",
      demoNote: "Demo cart — connect live Shopify products for real checkout.",
      checkoutError: "Could not start checkout. Please try again.",
    },

    // Cookie
    cookie: {
      text: "We use a few essential cookies so the cart works. Nothing more.",
      accept: "I understand",
      privacy: "Privacy",
    },

    // Footer
    footer: {
      tagline: "From the Baltic, with meaning. Amber, linen, and quiet objects from Latvia — carried to the world by hand.",
      discover: "Discover",
      care: "Care",
      storiesFromLatvia: "Stories from Latvia",
      newsletterHint: "A few times a year. No noise. Just stories from Latvia.",
      madeIn: "Made in Latvia",
      shop: "Shop",
      ourStory: "Our Story",
      latvia: "Latvia",
      journal: "Journal",
      contact: "Contact",
      shipping: "Shipping",
      faq: "FAQ",
      privacy: "Privacy",
    },

    // Newsletter
    newsletter: {
      hint: "A few times a year. No noise. Just stories from Latvia.",
      placeholder: "your@email.com",
      join: "Join",
      loading: "...",
      success: "Welcome. Stories from Latvia will arrive soon.",
      error: "Please use a valid email address.",
    },

    // Home
    home: {
      eyebrow: "From Latvia",
      heroTitle: "From the Baltic,\nwith meaning.",
      heroBody: "Amber washed from a 44-million-year-old sea. Linen woven since the Iron Age. Quiet objects from the eastern shore.",
      heroCta: "Explore the Collection",
      collectionsEyebrow: "Four collections",
      collectionsTitle: "Pieces of Latvia,\ncarried to the world.",
      featuredEyebrow: "A small selection",
      featuredTitle: "Made slowly. Carried gently.",
      featuredCta: "See the full shop",
      storyEyebrow: "Our Story",
      storyQuote: '"The amber she kept on her windowsill caught the morning light, every morning of my childhood."',
      storyBody: "BalticRoots began with a small wooden box. Inside: amber beads worn smooth, a linen shawl with folk patterns along the hem, a hand-carved Auseklis. My grandmother's things. The slow, honest world she lived in.",
      storyCta: "Read the full story",
      dainaEyebrow: "A Latvian Daina",
      dainaQuote: '"Saulīt tecēj\' jūriņā,\nDzintariņus dāvādama."',
      dainaTranslation: "The little sun ran into the sea, giving amber as a gift.",
      newsletterEyebrow: "Stories from Latvia",
      newsletterTitle: "A letter from the coast,\na few times a year.",
    },

    // Collections
    collections: {
      "amber-jewelry": { title: "Amber Jewelry", sub: "Dievakmeņi · Stones of the gods" },
      "linen-clothing": { title: "Linen Clothing", sub: "Iron Age fibre, slow loom" },
      "baltic-decor": { title: "Baltic Decor", sub: "Pine, beeswax, birch" },
      "gifts": { title: "Gifts", sub: "Wrapped in linen, signed by hand" },
    },

    // Shop
    shop: {
      eyebrow: "The Shop",
      title: "A small, considered selection.",
      body: "Everything here is made by hand, in small numbers, in or near Latvia. We stock pieces we would keep ourselves.",
      loading: "Quietly loading…",
      empty: "Nothing here just yet.",
      filters: {
        all: "All",
        amber: "Amber",
        linen: "Linen",
        decor: "Decor",
        gifts: "Gifts",
      },
    },

    // Product
    product: {
      addToBag: "Add to bag",
      size: "Size",
      madeIn: "Made in",
      madeInValue: "Latvia",
      shipping: "Shipping",
      shippingValue: "Worldwide · Free over €75",
      whyBaltic: "Why Baltic?",
      related: "You may also love",
      backToShop: "Back to the shop",
      loading: "Loading…",
      category: {
        "amber-jewelry": "amber jewelry",
        "linen-clothing": "linen clothing",
        "baltic-decor": "baltic decor",
        "gifts": "gifts",
      },
      added: (title) => `${title} added to your bag.`,
    },

    // Story
    story: {
      eyebrow: "Our Story",
      heroTitle: "A small wooden box.",
      p1: "I grew up spending summers at my grandmother's house near the Baltic coast in Latvia. The smell of pine and sea salt. The amber she kept on her windowsill that caught the morning light. The weight of a linen dress on a warm Jāņi evening. These things never left me.",
      p2: "When she passed, I inherited a small wooden box of her things. Amber beads worn smooth from years of touching. A linen shawl with Latvian folk patterns along the hem. A small hand-carved Auseklis — the morning star — that she kept by the door for protection.",
      p3: "I realised that this world — the slow, honest, natural world she lived in — was disappearing. Not just for me, but for everyone. We are all moving so fast that we forget what it feels like to hold something real.",
      p4: "BalticRoots was born from that box. It is Latvia sharing what it has always known — that beauty does not need to be invented. It already exists. In the amber the sea offers every morning. In the linen that breathes like a second skin. In the carved wood that smells of the forest it came from.",
      closing: "We do not sell products. We carry pieces of Latvia to the world.",
      values: {
        slowLiving: { eyebrow: "Slow Living", title: "No rush, no noise.", body: "Every piece is made by hand, in small numbers. We will never have a flash sale." },
        craft: { eyebrow: "Latvian Craft", title: "Made where it has always been made.", body: "We work with weaving cooperatives, amber families, and woodcarvers in Riga, Kurzeme, and Vidzeme." },
        amber: { eyebrow: "Amber from the Sea", title: "Forty-four million years.", body: "Our amber is gathered after winter storms along the white sand dunes of Jūrmala. Untreated. Honest." },
        linen: { eyebrow: "Linen from the Fields", title: "Flax to fabric in 200 km.", body: "From seed to seam, our linen never leaves the Baltic region. Grown, retted, spun, sewn — all in Latvia." },
      },
      ctaTitle: "Carry a piece home.",
      ctaBtn: "Explore the shop",
    },

    // Latvia
    latvia: {
      eyebrow: "The Land Behind BalticRoots",
      heroTitle: "Latvia.",
      heroBody: "The eastern shore of the Baltic Sea. Pine forest, white sand, amber and linen.",
    },

    // Journal
    journal: {
      eyebrow: "The Journal",
      heroTitle: "Letters from the coast.",
      heroBody: "Short stories about amber, linen, the Latvian seasons, and the people who still work by hand.",
      readStory: "Read the story →",
      backToJournal: "← Back to the journal",
      eyebrowLabel: "Journal",
    },

    // Contact
    contact: {
      eyebrow: "Contact",
      title: "Write to us.\nQuietly.",
      body: "We answer every message within two working days. We are based in Riga, Latvia, and we ship worldwide.",
      emailLabel: "Email",
      studioLabel: "Studio",
      studioValue: "Vecrīga, Riga, Latvia",
      nameLabel: "Your name",
      emailFieldLabel: "Email",
      messageLabel: "Message",
      submit: "Send message",
      sending: "Sending…",
      success: "Thank you. We will reply within two working days.",
      error: "Could not send your message. Please try again.",
    },

    // FAQ
    faq: {
      eyebrow: "Frequently Asked",
      title: "Questions, answered.",
      items: [
        { q: "How long will my order take?", a: "We ship from Riga, Latvia. Inside the EU: 3–6 working days. UK and Switzerland: 4–8 working days. Worldwide: 7–14 working days. Free shipping on orders over €75." },
        { q: "Is the amber real Baltic amber?", a: "Yes. Every piece is genuine Baltic amber, gathered or sourced within Latvia. We provide a small card with each amber piece telling you the region it came from. We never sell pressed amber or copal." },
        { q: "How should I care for my linen?", a: "Linen softens beautifully with age. Wash cool (30°C) on a gentle cycle, line dry, iron while slightly damp if you wish. Real Latvian linen will outlive most things in your wardrobe." },
        { q: "Do you ship worldwide?", a: "Yes. We ship to the EU, UK, USA, Canada, Australia, Japan, and most other countries. Duties and taxes outside the EU are calculated at checkout where possible, or paid on delivery." },
        { q: "What is your return policy?", a: "Returns accepted within 30 days of delivery, unworn and in original packaging. Return shipping is at the customer's expense unless the item is faulty. Email hello@balticroots.store before returning." },
        { q: "How do I find my linen size?", a: "Our linen is cut loose by design. Most pieces fit one size larger than standard European sizing. Detailed measurements are on each product page. If in doubt, write to us." },
        { q: "Which payments do you accept?", a: "All major cards, Apple Pay, Google Pay, Klarna, and bank transfer for EU orders. All payments are processed securely through Shopify Payments and Stripe." },
      ],
    },

    // Shipping
    shipping: {
      eyebrow: "Shipping & Returns",
      title: "From Riga, to you.",
      sections: [
        { title: "Where we ship", body: "We ship from Riga, Latvia, to the European Union, the United Kingdom, the United States, Canada, Australia, Japan, and most other countries. If your country is not listed at checkout, write to us — we will usually find a way." },
        { title: "Delivery times", body: "European Union: 3–6 working days. United Kingdom & Switzerland: 4–8 working days. Worldwide: 7–14 working days. Free shipping on orders over €75 inside the EU and over €120 worldwide." },
        { title: "Packaging", body: "Every piece is wrapped in undyed linen and tied with natural twine. No plastic. No foam. No noise." },
        { title: "Returns", body: "Returns are accepted within 30 days of delivery, unworn and in their original packaging. Return shipping is paid by the customer unless the item is faulty. Refunds are processed within 5 working days of receiving the return." },
        { title: "Duties and taxes", body: "EU orders include VAT. UK orders are shipped DDP — duties paid by us. Orders outside the EU and UK may be subject to local import duties payable on delivery." },
        { title: "Lost in the post", body: "If your order has not arrived within the expected window, please write to hello@balticroots.store and we will trace it personally." },
      ],
    },

    // Privacy
    privacy: {
      eyebrow: "Privacy",
      title: "Quietly handled.",
      updated: "Last updated: February 2026",
      sections: [
        { title: "What we collect", body: "When you place an order or sign up to our journal, we collect your name, email, shipping address, and the items you bought. That is all." },
        { title: "Why we collect it", body: "To send you your order. To answer your questions. To send you a few times a year — only if you have asked us to — a letter from the coast." },
        { title: "Who we share it with", body: "Our payment processor (Shopify Payments / Stripe), our shipping carriers (Latvijas Pasts, DPD, DHL), and our newsletter system. No one else. We do not sell or rent your data." },
        { title: "GDPR", body: "Latvia is in the European Union. You have the right to ask what we hold about you, to correct it, to have it deleted, and to take it elsewhere. Write to hello@balticroots.store and we will respond within 30 days." },
        { title: "Cookies", body: "We use only essential cookies — the ones the cart needs to remember what you have chosen. No tracking. No advertising pixels." },
      ],
    },
  },

  // ─────────────────────────── DEUTSCH ───────────────────────────
  DE: {
    announcement: "Kostenloser Versand ab €75  ·  Bernstein aus der Ostsee  ·  Authentisches lettisches Handwerk",

    nav: {
      shop: "Shop",
      ourStory: "Unsere Geschichte",
      latvia: "Lettland",
      journal: "Journal",
    },

    cart: {
      yourBag: "Ihre Tasche",
      empty: "Ihre Tasche ist noch leer.",
      subtotal: "Zwischensumme",
      checkout: "Zur Kasse",
      taxNote: "Steuern und Versand werden an der Kasse berechnet",
      remove: "Entfernen",
      demoNote: "Demo-Warenkorb — verbinden Sie echte Shopify-Produkte für echten Checkout.",
      checkoutError: "Checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
    },

    cookie: {
      text: "Wir verwenden einige notwendige Cookies, damit der Warenkorb funktioniert. Mehr nicht.",
      accept: "Ich verstehe",
      privacy: "Datenschutz",
    },

    footer: {
      tagline: "Aus dem Baltikum, mit Bedeutung. Bernstein, Leinen und stille Objekte aus Lettland — von Hand in die Welt getragen.",
      discover: "Entdecken",
      care: "Service",
      storiesFromLatvia: "Geschichten aus Lettland",
      newsletterHint: "Ein paar Mal im Jahr. Kein Lärm. Nur Geschichten aus Lettland.",
      madeIn: "Hergestellt in Lettland",
      shop: "Shop",
      ourStory: "Unsere Geschichte",
      latvia: "Lettland",
      journal: "Journal",
      contact: "Kontakt",
      shipping: "Versand",
      faq: "FAQ",
      privacy: "Datenschutz",
    },

    newsletter: {
      hint: "Ein paar Mal im Jahr. Kein Lärm. Nur Geschichten aus Lettland.",
      placeholder: "ihre@email.com",
      join: "Anmelden",
      loading: "...",
      success: "Willkommen. Geschichten aus Lettland kommen bald.",
      error: "Bitte verwenden Sie eine gültige E-Mail-Adresse.",
    },

    home: {
      eyebrow: "Aus Lettland",
      heroTitle: "Aus dem Baltikum,\nmit Bedeutung.",
      heroBody: "Bernstein, gespült von einem 44 Millionen Jahre alten Meer. Leinen, seit der Eisenzeit gewoben. Stille Objekte vom östlichen Ufer.",
      heroCta: "Kollektion erkunden",
      collectionsEyebrow: "Vier Kollektionen",
      collectionsTitle: "Stücke aus Lettland,\nin die Welt getragen.",
      featuredEyebrow: "Eine kleine Auswahl",
      featuredTitle: "Langsam gemacht. Sanft getragen.",
      featuredCta: "Zum vollständigen Shop",
      storyEyebrow: "Unsere Geschichte",
      storyQuote: '"Der Bernstein, den sie auf ihrer Fensterbank aufbewahrte, fing jeden Morgen das Morgenlicht — jeden Morgen meiner Kindheit."',
      storyBody: "BalticRoots begann mit einer kleinen Holzschachtel. Darin: Bernsteinperlen, glatt getragen von Jahren des Anfassens. Ein Leinenschal mit lettischen Volksmustern am Saum. Ein handgeschnitzter Auseklis. Die Dinge meiner Großmutter. Die langsame, ehrliche Welt, in der sie lebte.",
      storyCta: "Die ganze Geschichte lesen",
      dainaEyebrow: "Eine lettische Daina",
      dainaQuote: '"Saulīt tecēj\' jūriņā,\nDzintariņus dāvādama."',
      dainaTranslation: "Die kleine Sonne lief ins Meer und schenkte Bernstein als Gabe.",
      newsletterEyebrow: "Geschichten aus Lettland",
      newsletterTitle: "Ein Brief von der Küste,\nein paar Mal im Jahr.",
    },

    collections: {
      "amber-jewelry": { title: "Bernsteinschmuck", sub: "Dievakmeņi · Steine der Götter" },
      "linen-clothing": { title: "Leinenkleidung", sub: "Eisenzeitfaser, langsamer Webstuhl" },
      "baltic-decor": { title: "Baltische Dekoration", sub: "Kiefer, Bienenwachs, Birke" },
      "gifts": { title: "Geschenke", sub: "In Leinen gewickelt, von Hand signiert" },
    },

    shop: {
      eyebrow: "Der Shop",
      title: "Eine kleine, bedachte Auswahl.",
      body: "Alles hier ist handgefertigt, in kleinen Stückzahlen, in oder nahe Lettland. Wir führen Stücke, die wir selbst behalten würden.",
      loading: "Lädt leise…",
      empty: "Hier ist noch nichts.",
      filters: {
        all: "Alle",
        amber: "Bernstein",
        linen: "Leinen",
        decor: "Deko",
        gifts: "Geschenke",
      },
    },

    product: {
      addToBag: "In die Tasche",
      size: "Größe",
      madeIn: "Hergestellt in",
      madeInValue: "Lettland",
      shipping: "Versand",
      shippingValue: "Weltweit · Kostenlos ab €75",
      whyBaltic: "Warum baltisch?",
      related: "Das könnte Ihnen auch gefallen",
      backToShop: "Zurück zum Shop",
      loading: "Lädt…",
      category: {
        "amber-jewelry": "Bernsteinschmuck",
        "linen-clothing": "Leinenkleidung",
        "baltic-decor": "Baltische Dekoration",
        "gifts": "Geschenke",
      },
      added: (title) => `${title} wurde Ihrer Tasche hinzugefügt.`,
    },

    story: {
      eyebrow: "Unsere Geschichte",
      heroTitle: "Eine kleine Holzschachtel.",
      p1: "Ich verbrachte die Sommer meiner Kindheit im Haus meiner Großmutter in der Nähe der lettischen Ostseeküste. Der Geruch von Kiefer und Meersalz. Der Bernstein, den sie auf ihrer Fensterbank aufbewahrte und der das Morgenlicht einfing. Das Gewicht eines Leinenkleides an einem warmen Jāņi-Abend. Diese Dinge haben mich nie verlassen.",
      p2: "Als sie starb, erbte ich eine kleine Holzschachtel mit ihren Sachen. Bernsteinperlen, glatt getragen von Jahren des Anfassens. Ein Leinenschal mit lettischen Volksmustern am Saum. Ein kleiner handgeschnitzter Auseklis — der Morgenstern — den sie zum Schutz bei der Tür aufbewahrte.",
      p3: "Ich erkannte, dass diese Welt — die langsame, ehrliche, natürliche Welt, in der sie lebte — verschwand. Nicht nur für mich, sondern für alle.",
      p4: "BalticRoots wurde aus dieser Schachtel geboren. Es ist Lettland, das teilt, was es immer gewusst hat — dass Schönheit nicht erfunden werden muss.",
      closing: "Wir verkaufen keine Produkte. Wir tragen Stücke Lettlands in die Welt.",
      values: {
        slowLiving: { eyebrow: "Langsames Leben", title: "Kein Eile, kein Lärm.", body: "Jedes Stück ist von Hand gefertigt, in kleinen Stückzahlen. Wir werden nie einen Blitzverkauf haben." },
        craft: { eyebrow: "Lettisches Handwerk", title: "Dort gemacht, wo es immer gemacht wurde.", body: "Wir arbeiten mit Webgenossenschaften, Bernsteinfamilien und Holzschnitzern in Riga, Kurzeme und Vidzeme." },
        amber: { eyebrow: "Bernstein aus dem Meer", title: "Vierundvierzig Millionen Jahre.", body: "Unser Bernstein wird nach Winterstürmen entlang der weißen Sanddünen von Jūrmala gesammelt. Unbehandelt. Ehrlich." },
        linen: { eyebrow: "Leinen aus den Feldern", title: "Vom Flachs zum Stoff in 200 km.", body: "Von der Saat bis zur Naht verlässt unser Leinen nie die Baltikumsregion. Angebaut, geröstet, gesponnen, genäht — alles in Lettland." },
      },
      ctaTitle: "Ein Stück nach Hause tragen.",
      ctaBtn: "Shop erkunden",
    },

    latvia: {
      eyebrow: "Das Land hinter BalticRoots",
      heroTitle: "Lettland.",
      heroBody: "Das östliche Ufer der Ostsee. Kiefernwald, weißer Sand, Bernstein und Leinen.",
    },

    journal: {
      eyebrow: "Das Journal",
      heroTitle: "Briefe von der Küste.",
      heroBody: "Kurze Geschichten über Bernstein, Leinen, die lettischen Jahreszeiten und die Menschen, die noch von Hand arbeiten.",
      readStory: "Geschichte lesen →",
      backToJournal: "← Zurück zum Journal",
      eyebrowLabel: "Journal",
    },

    contact: {
      eyebrow: "Kontakt",
      title: "Schreiben Sie uns.\nLeise.",
      body: "Wir beantworten jede Nachricht innerhalb von zwei Werktagen. Wir befinden uns in Riga, Lettland, und versenden weltweit.",
      emailLabel: "E-Mail",
      studioLabel: "Atelier",
      studioValue: "Vecrīga, Riga, Lettland",
      nameLabel: "Ihr Name",
      emailFieldLabel: "E-Mail",
      messageLabel: "Nachricht",
      submit: "Nachricht senden",
      sending: "Wird gesendet…",
      success: "Vielen Dank. Wir antworten innerhalb von zwei Werktagen.",
      error: "Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
    },

    faq: {
      eyebrow: "Häufig gestellte Fragen",
      title: "Fragen, beantwortet.",
      items: [
        { q: "Wie lange dauert meine Bestellung?", a: "Wir versenden aus Riga, Lettland. Innerhalb der EU: 3–6 Werktage. Großbritannien und Schweiz: 4–8 Werktage. Weltweit: 7–14 Werktage. Kostenloser Versand bei Bestellungen über €75." },
        { q: "Ist der Bernstein echter Baltischer Bernstein?", a: "Ja. Jedes Stück ist echter Baltischer Bernstein, gesammelt oder bezogen aus Lettland. Wir verkaufen niemals gepressten Bernstein oder Kopal." },
        { q: "Wie pflege ich mein Leinen?", a: "Leinen wird mit der Zeit wunderschön weich. Waschen Sie es kalt (30°C) im Schonwaschgang, lassen Sie es lufttrocknen und bügeln Sie es bei Bedarf leicht feucht." },
        { q: "Versenden Sie weltweit?", a: "Ja. Wir versenden in die EU, nach Großbritannien, USA, Kanada, Australien, Japan und die meisten anderen Länder." },
        { q: "Was ist Ihre Rückgaberichtlinie?", a: "Rückgaben werden innerhalb von 30 Tagen nach Lieferung akzeptiert, ungetragen und in der Originalverpackung. Schreiben Sie an hello@balticroots.store." },
        { q: "Wie finde ich meine Leinengröße?", a: "Unser Leinen ist bewusst weit geschnitten. Die meisten Stücke passen eine Größe größer als die europäische Standardgröße. Detaillierte Maße finden Sie auf jeder Produktseite." },
        { q: "Welche Zahlungsmethoden akzeptieren Sie?", a: "Alle gängigen Karten, Apple Pay, Google Pay, Klarna und Banküberweisung für EU-Bestellungen." },
      ],
    },

    shipping: {
      eyebrow: "Versand & Rückgabe",
      title: "Von Riga zu Ihnen.",
      sections: [
        { title: "Wohin wir versenden", body: "Wir versenden aus Riga, Lettland, in die Europäische Union, das Vereinigte Königreich, die USA, Kanada, Australien, Japan und die meisten anderen Länder." },
        { title: "Lieferzeiten", body: "Europäische Union: 3–6 Werktage. Vereinigtes Königreich & Schweiz: 4–8 Werktage. Weltweit: 7–14 Werktage. Kostenloser Versand bei Bestellungen über €75 in der EU." },
        { title: "Verpackung", body: "Jedes Stück wird in ungebleichtem Leinen eingewickelt und mit Naturzwirn gebunden. Kein Plastik. Kein Styropor. Kein Lärm." },
        { title: "Rückgaben", body: "Rückgaben werden innerhalb von 30 Tagen nach Lieferung akzeptiert, ungetragen und in der Originalverpackung. Erstattungen werden innerhalb von 5 Werktagen nach Erhalt der Rückgabe bearbeitet." },
        { title: "Zölle und Steuern", body: "EU-Bestellungen enthalten MwSt. Bestellungen außerhalb der EU und des Vereinigten Königreichs können lokalen Einfuhrzöllen unterliegen." },
        { title: "Auf dem Postweg verloren", body: "Wenn Ihre Bestellung nicht innerhalb des erwarteten Zeitrahmens angekommen ist, schreiben Sie bitte an hello@balticroots.store." },
      ],
    },

    privacy: {
      eyebrow: "Datenschutz",
      title: "Leise behandelt.",
      updated: "Zuletzt aktualisiert: Februar 2026",
      sections: [
        { title: "Was wir sammeln", body: "Wenn Sie eine Bestellung aufgeben oder sich für unser Journal anmelden, erfassen wir Ihren Namen, Ihre E-Mail, Lieferadresse und die gekauften Artikel." },
        { title: "Warum wir es sammeln", body: "Um Ihre Bestellung zu senden. Um Ihre Fragen zu beantworten. Um Ihnen ein paar Mal im Jahr einen Brief von der Küste zu schicken — nur wenn Sie darum gebeten haben." },
        { title: "Mit wem wir es teilen", body: "Unser Zahlungsanbieter (Shopify Payments / Stripe), unsere Versanddienstleister und unser Newsletter-System. Niemand sonst." },
        { title: "DSGVO", body: "Lettland ist in der Europäischen Union. Sie haben das Recht zu erfragen, was wir über Sie gespeichert haben, es zu korrigieren, löschen zu lassen und mitzunehmen." },
        { title: "Cookies", body: "Wir verwenden nur notwendige Cookies — diejenigen, die der Warenkorb benötigt. Kein Tracking. Keine Werbepixel." },
      ],
    },
  },

  // ─────────────────────────── FRANÇAIS ───────────────────────────
  FR: {
    announcement: "Livraison gratuite dès €75  ·  Ambre de la mer Baltique  ·  Artisanat letton authentique",

    nav: {
      shop: "Boutique",
      ourStory: "Notre Histoire",
      latvia: "Lettonie",
      journal: "Journal",
    },

    cart: {
      yourBag: "Votre Panier",
      empty: "Votre panier est vide pour l'instant.",
      subtotal: "Sous-total",
      checkout: "Commander",
      taxNote: "Taxes et livraison calculées à la commande",
      remove: "Retirer",
      demoNote: "Panier démo — connectez de vrais produits Shopify pour un vrai paiement.",
      checkoutError: "Impossible de démarrer le paiement. Veuillez réessayer.",
    },

    cookie: {
      text: "Nous utilisons quelques cookies essentiels pour que le panier fonctionne. Rien de plus.",
      accept: "Je comprends",
      privacy: "Confidentialité",
    },

    footer: {
      tagline: "De la Baltique, avec sens. Ambre, lin et objets silencieux de Lettonie — portés au monde à la main.",
      discover: "Découvrir",
      care: "Service",
      storiesFromLatvia: "Histoires de Lettonie",
      newsletterHint: "Quelques fois par an. Sans bruit. Juste des histoires de Lettonie.",
      madeIn: "Fabriqué en Lettonie",
      shop: "Boutique",
      ourStory: "Notre Histoire",
      latvia: "Lettonie",
      journal: "Journal",
      contact: "Contact",
      shipping: "Livraison",
      faq: "FAQ",
      privacy: "Confidentialité",
    },

    newsletter: {
      hint: "Quelques fois par an. Sans bruit. Juste des histoires de Lettonie.",
      placeholder: "votre@email.com",
      join: "Rejoindre",
      loading: "...",
      success: "Bienvenue. Les histoires de Lettonie arriveront bientôt.",
      error: "Veuillez utiliser une adresse e-mail valide.",
    },

    home: {
      eyebrow: "De Lettonie",
      heroTitle: "De la Baltique,\navec sens.",
      heroBody: "Ambre lavée par une mer vieille de 44 millions d'années. Lin tissé depuis l'Âge du Fer. Objets silencieux de la rive orientale.",
      heroCta: "Explorer la Collection",
      collectionsEyebrow: "Quatre collections",
      collectionsTitle: "Pièces de Lettonie,\nportées au monde.",
      featuredEyebrow: "Une petite sélection",
      featuredTitle: "Fait lentement. Porté doucement.",
      featuredCta: "Voir toute la boutique",
      storyEyebrow: "Notre Histoire",
      storyQuote: '"L\'ambre qu\'elle gardait sur son rebord de fenêtre captait la lumière du matin, chaque matin de mon enfance."',
      storyBody: "BalticRoots a commencé avec une petite boîte en bois. Dedans : des perles d'ambre lissées par des années de contact. Un châle en lin avec des motifs folkloriques lettons au bord. Un Auseklis sculpté à la main. Les affaires de ma grand-mère.",
      storyCta: "Lire l'histoire complète",
      dainaEyebrow: "Une Daina lettonne",
      dainaQuote: '"Saulīt tecēj\' jūriņā,\nDzintariņus dāvādama."',
      dainaTranslation: "Le petit soleil courut dans la mer, offrant de l'ambre en cadeau.",
      newsletterEyebrow: "Histoires de Lettonie",
      newsletterTitle: "Une lettre de la côte,\nquelques fois par an.",
    },

    collections: {
      "amber-jewelry": { title: "Bijoux en Ambre", sub: "Dievakmeņi · Pierres des dieux" },
      "linen-clothing": { title: "Vêtements en Lin", sub: "Fibre de l'Âge du Fer, métier lent" },
      "baltic-decor": { title: "Décoration Baltique", sub: "Pin, cire d'abeille, bouleau" },
      "gifts": { title: "Cadeaux", sub: "Enveloppé dans du lin, signé à la main" },
    },

    shop: {
      eyebrow: "La Boutique",
      title: "Une petite sélection réfléchie.",
      body: "Tout ici est fabriqué à la main, en petites quantités, en Lettonie ou à proximité. Nous stockons des pièces que nous garderions nous-mêmes.",
      loading: "Chargement silencieux…",
      empty: "Rien ici pour l'instant.",
      filters: {
        all: "Tout",
        amber: "Ambre",
        linen: "Lin",
        decor: "Déco",
        gifts: "Cadeaux",
      },
    },

    product: {
      addToBag: "Ajouter au panier",
      size: "Taille",
      madeIn: "Fabriqué en",
      madeInValue: "Lettonie",
      shipping: "Livraison",
      shippingValue: "Mondial · Gratuit dès €75",
      whyBaltic: "Pourquoi baltique ?",
      related: "Vous pourriez aussi aimer",
      backToShop: "Retour à la boutique",
      loading: "Chargement…",
      category: {
        "amber-jewelry": "bijoux en ambre",
        "linen-clothing": "vêtements en lin",
        "baltic-decor": "décoration baltique",
        "gifts": "cadeaux",
      },
      added: (title) => `${title} ajouté à votre panier.`,
    },

    story: {
      eyebrow: "Notre Histoire",
      heroTitle: "Une petite boîte en bois.",
      p1: "J'ai grandi en passant mes étés dans la maison de ma grand-mère près de la côte baltique en Lettonie. L'odeur de pin et de sel marin. L'ambre qu'elle gardait sur son rebord de fenêtre qui captait la lumière du matin. Le poids d'une robe en lin lors d'une soirée chaude de Jāņi. Ces choses ne m'ont jamais quitté.",
      p2: "Quand elle est décédée, j'ai hérité d'une petite boîte en bois de ses affaires. Des perles d'ambre lissées par des années de contact. Un châle en lin avec des motifs folkloriques lettons. Un petit Auseklis sculpté à la main — l'étoile du matin — qu'elle gardait à la porte pour la protection.",
      p3: "J'ai réalisé que ce monde — le monde lent, honnête et naturel dans lequel elle vivait — disparaissait. Pas seulement pour moi, mais pour tout le monde.",
      p4: "BalticRoots est né de cette boîte. C'est la Lettonie qui partage ce qu'elle a toujours su — que la beauté n'a pas besoin d'être inventée.",
      closing: "Nous ne vendons pas des produits. Nous portons des morceaux de Lettonie au monde.",
      values: {
        slowLiving: { eyebrow: "Vie Lente", title: "Pas de hâte, pas de bruit.", body: "Chaque pièce est faite à la main, en petites quantités. Nous n'aurons jamais de vente flash." },
        craft: { eyebrow: "Artisanat Letton", title: "Fait là où il a toujours été fait.", body: "Nous travaillons avec des coopératives de tissage, des familles d'ambre et des sculpteurs sur bois à Riga, Kurzeme et Vidzeme." },
        amber: { eyebrow: "Ambre de la Mer", title: "Quarante-quatre millions d'années.", body: "Notre ambre est recueillie après les tempêtes hivernales le long des dunes de sable blanc de Jūrmala. Non traitée. Honnête." },
        linen: { eyebrow: "Lin des Champs", title: "Du lin au tissu en 200 km.", body: "De la graine à la couture, notre lin ne quitte jamais la région baltique. Cultivé, rouissé, filé, cousu — tout en Lettonie." },
      },
      ctaTitle: "Emportez un morceau chez vous.",
      ctaBtn: "Explorer la boutique",
    },

    latvia: {
      eyebrow: "Le Pays derrière BalticRoots",
      heroTitle: "Lettonie.",
      heroBody: "La rive orientale de la mer Baltique. Forêt de pins, sable blanc, ambre et lin.",
    },

    journal: {
      eyebrow: "Le Journal",
      heroTitle: "Lettres de la côte.",
      heroBody: "De courtes histoires sur l'ambre, le lin, les saisons lettonnes et les gens qui travaillent encore à la main.",
      readStory: "Lire l'histoire →",
      backToJournal: "← Retour au journal",
      eyebrowLabel: "Journal",
    },

    contact: {
      eyebrow: "Contact",
      title: "Écrivez-nous.\nDoucement.",
      body: "Nous répondons à chaque message dans les deux jours ouvrables. Nous sommes basés à Riga, Lettonie, et nous expédions dans le monde entier.",
      emailLabel: "E-mail",
      studioLabel: "Studio",
      studioValue: "Vecrīga, Riga, Lettonie",
      nameLabel: "Votre nom",
      emailFieldLabel: "E-mail",
      messageLabel: "Message",
      submit: "Envoyer le message",
      sending: "Envoi en cours…",
      success: "Merci. Nous répondrons dans les deux jours ouvrables.",
      error: "Impossible d'envoyer votre message. Veuillez réessayer.",
    },

    faq: {
      eyebrow: "Questions Fréquentes",
      title: "Questions, répondues.",
      items: [
        { q: "Combien de temps prendra ma commande ?", a: "Nous expédions depuis Riga, Lettonie. Dans l'UE : 3–6 jours ouvrables. Royaume-Uni et Suisse : 4–8 jours ouvrables. Mondial : 7–14 jours ouvrables. Livraison gratuite pour les commandes de plus de €75." },
        { q: "L'ambre est-elle de la vraie ambre baltique ?", a: "Oui. Chaque pièce est de la vraie ambre baltique, collectée ou sourcée en Lettonie. Nous ne vendons jamais d'ambre pressée ou de copal." },
        { q: "Comment entretenir mon lin ?", a: "Le lin s'adoucit magnifiquement avec l'âge. Lavez-le froid (30°C) en cycle délicat, séchez-le à l'air libre, repassez-le légèrement humide si vous le souhaitez." },
        { q: "Livrez-vous dans le monde entier ?", a: "Oui. Nous livrons dans l'UE, au Royaume-Uni, aux USA, au Canada, en Australie, au Japon et dans la plupart des autres pays." },
        { q: "Quelle est votre politique de retour ?", a: "Retours acceptés dans les 30 jours suivant la livraison, non portés et dans l'emballage d'origine. Écrivez à hello@balticroots.store avant de retourner." },
        { q: "Comment trouver ma taille de lin ?", a: "Notre lin est coupé ample par conception. La plupart des pièces conviennent à une taille plus grande que la taille européenne standard." },
        { q: "Quels modes de paiement acceptez-vous ?", a: "Toutes les grandes cartes, Apple Pay, Google Pay, Klarna et virement bancaire pour les commandes UE." },
      ],
    },

    shipping: {
      eyebrow: "Livraison & Retours",
      title: "De Riga, à vous.",
      sections: [
        { title: "Où nous livrons", body: "Nous expédions depuis Riga, Lettonie, vers l'Union européenne, le Royaume-Uni, les États-Unis, le Canada, l'Australie, le Japon et la plupart des autres pays." },
        { title: "Délais de livraison", body: "Union européenne : 3–6 jours ouvrables. Royaume-Uni & Suisse : 4–8 jours ouvrables. Mondial : 7–14 jours ouvrables." },
        { title: "Emballage", body: "Chaque pièce est enveloppée dans du lin non teint et attachée avec de la ficelle naturelle. Pas de plastique. Pas de mousse. Pas de bruit." },
        { title: "Retours", body: "Les retours sont acceptés dans les 30 jours suivant la livraison, non portés et dans leur emballage d'origine." },
        { title: "Droits et taxes", body: "Les commandes UE incluent la TVA. Les commandes en dehors de l'UE peuvent être soumises à des droits d'importation locaux." },
        { title: "Perdu dans la poste", body: "Si votre commande n'est pas arrivée dans le délai prévu, écrivez à hello@balticroots.store et nous le retrouverons personnellement." },
      ],
    },

    privacy: {
      eyebrow: "Confidentialité",
      title: "Géré discrètement.",
      updated: "Dernière mise à jour : Février 2026",
      sections: [
        { title: "Ce que nous collectons", body: "Lorsque vous passez une commande ou vous inscrivez à notre journal, nous collectons votre nom, e-mail, adresse de livraison et les articles achetés." },
        { title: "Pourquoi nous le collectons", body: "Pour vous envoyer votre commande. Pour répondre à vos questions. Pour vous envoyer quelques fois par an — seulement si vous l'avez demandé — une lettre de la côte." },
        { title: "Avec qui nous le partageons", body: "Notre processeur de paiement, nos transporteurs et notre système de newsletter. Personne d'autre. Nous ne vendons ni ne louons vos données." },
        { title: "RGPD", body: "La Lettonie est dans l'Union européenne. Vous avez le droit de demander ce que nous détenons sur vous, de le corriger, de le faire supprimer." },
        { title: "Cookies", body: "Nous utilisons uniquement des cookies essentiels — ceux dont le panier a besoin. Pas de suivi. Pas de pixels publicitaires." },
      ],
    },
  },

  // ─────────────────────────── LATVIEŠU ───────────────────────────
  LV: {
    announcement: "Bezmaksas piegāde no €75  ·  Dzintars no Baltijas jūras  ·  Īsts latviešu amatniecības darbs",

    nav: {
      shop: "Veikals",
      ourStory: "Mūsu Stāsts",
      latvia: "Latvija",
      journal: "Žurnāls",
    },

    cart: {
      yourBag: "Jūsu Soma",
      empty: "Jūsu soma pagaidām ir tukša.",
      subtotal: "Starpsumma",
      checkout: "Norēķināties",
      taxNote: "Nodokļi un piegāde tiek aprēķināti norēķinoties",
      remove: "Noņemt",
      demoNote: "Demo grozs — savienojiet īstus Shopify produktus īstai izrakstīšanās.",
      checkoutError: "Nevarēja sākt izrakstīšanos. Lūdzu, mēģiniet vēlreiz.",
    },

    cookie: {
      text: "Mēs izmantojam dažus būtiskus sīkfailus, lai grozs darbotos. Nekas cits.",
      accept: "Saprotu",
      privacy: "Privātums",
    },

    footer: {
      tagline: "No Baltijas, ar jēgu. Dzintars, lins un klusi priekšmeti no Latvijas — ar rokām aiznesti pasaulē.",
      discover: "Atklāt",
      care: "Aprūpe",
      storiesFromLatvia: "Stāsti no Latvijas",
      newsletterHint: "Dažas reizes gadā. Bez trokšņa. Tikai stāsti no Latvijas.",
      madeIn: "Ražots Latvijā",
      shop: "Veikals",
      ourStory: "Mūsu Stāsts",
      latvia: "Latvija",
      journal: "Žurnāls",
      contact: "Kontakti",
      shipping: "Piegāde",
      faq: "BUJ",
      privacy: "Privātums",
    },

    newsletter: {
      hint: "Dažas reizes gadā. Bez trokšņa. Tikai stāsti no Latvijas.",
      placeholder: "jūsu@epasts.lv",
      join: "Pievienoties",
      loading: "...",
      success: "Laipni lūgts. Drīz pienāks stāsti no Latvijas.",
      error: "Lūdzu, ievadiet derīgu e-pasta adresi.",
    },

    home: {
      eyebrow: "No Latvijas",
      heroTitle: "No Baltijas,\nar jēgu.",
      heroBody: "Dzintars no 44 miljonu gadus vecas jūras. Lins austs kopš dzelzs laikmeta. Klusi priekšmeti no austrumu krasta.",
      heroCta: "Izpētīt Kolekciju",
      collectionsEyebrow: "Četras kolekcijas",
      collectionsTitle: "Latvijas gabali,\naiznesti pasaulē.",
      featuredEyebrow: "Neliela izlase",
      featuredTitle: "Darināts lēni. Nests saudzīgi.",
      featuredCta: "Skatīt visu veikalu",
      storyEyebrow: "Mūsu Stāsts",
      storyQuote: '"Dzintars, ko viņa glabāja uz palodzes, ķēra rīta gaismu — katru manas bērnības rītu."',
      storyBody: "BalticRoots sākās ar nelielu koka kastīti. Iekšā: dzintara pērles, noglumētas gadiem ilgas turēšanas. Lina lakats ar tautas rakstiem. Ar roku griezts Auseklis. Manas vecmāmiņas lietas.",
      storyCta: "Lasīt pilno stāstu",
      dainaEyebrow: "Latviešu Daina",
      dainaQuote: '"Saulīt tecēj\' jūriņā,\nDzintariņus dāvādama."',
      dainaTranslation: "Saulīte tecēja jūrā, dāvādama dzintariņus.",
      newsletterEyebrow: "Stāsti no Latvijas",
      newsletterTitle: "Vēstule no krasta,\ndažas reizes gadā.",
    },

    collections: {
      "amber-jewelry": { title: "Dzintara Rotaslietas", sub: "Dievakmeņi · Dievu akmeņi" },
      "linen-clothing": { title: "Lina Apģērbs", sub: "Dzelzs laikmeta šķiedra, lēna stellē" },
      "baltic-decor": { title: "Baltijas Dekors", sub: "Priede, bišu vasks, bērzs" },
      "gifts": { title: "Dāvanas", sub: "Ietīts linā, parakstīts ar roku" },
    },

    shop: {
      eyebrow: "Veikals",
      title: "Neliela, pārdomāta izlase.",
      body: "Viss šeit ir darināts ar rokām, nelielā skaitā, Latvijā vai tās tuvumā. Mēs glabājam gabalus, ko paši paturētu.",
      loading: "Klusi ielādē…",
      empty: "Šeit pagaidām nekas nav.",
      filters: {
        all: "Visi",
        amber: "Dzintars",
        linen: "Lins",
        decor: "Dekors",
        gifts: "Dāvanas",
      },
    },

    product: {
      addToBag: "Pievienot somai",
      size: "Izmērs",
      madeIn: "Ražots",
      madeInValue: "Latvijā",
      shipping: "Piegāde",
      shippingValue: "Visā pasaulē · Bezmaksas no €75",
      whyBaltic: "Kāpēc Baltijas?",
      related: "Jums varētu patikt arī",
      backToShop: "Atpakaļ uz veikalu",
      loading: "Ielādē…",
      category: {
        "amber-jewelry": "dzintara rotaslietas",
        "linen-clothing": "lina apģērbs",
        "baltic-decor": "baltijas dekors",
        "gifts": "dāvanas",
      },
      added: (title) => `${title} pievienots jūsu somai.`,
    },

    story: {
      eyebrow: "Mūsu Stāsts",
      heroTitle: "Neliela koka kastīte.",
      p1: "Es uzaugu pavadot vasaras vecmāmiņas mājā pie Latvijas Baltijas krasta. Priedes un jūras sāls smarža. Dzintars, ko viņa glabāja uz palodzes un kas ķēra rīta gaismu. Lina kleitas smagums siltā Jāņu vakarā. Šīs lietas mani nekad neatstāja.",
      p2: "Kad viņa aizgāja, es mantoju nelielu koka kastīti ar viņas lietām. Dzintara pērles, noglumētas gadiem ilgas turēšanas. Lina lakats ar latviešu tautas rakstiem. Ar roku griezts Auseklis — rīta zvaigzne — ko viņa glabāja pie durvīm aizsardzībai.",
      p3: "Es sapratu, ka šī pasaule — lēnā, godīgā, dabiskā pasaule, kurā viņa dzīvoja — izzūd. Ne tikai man, bet visiem.",
      p4: "BalticRoots dzima no tās kastītes. Tā ir Latvija, daloties ar to, ko tā vienmēr ir zinājusi — ka skaistums nav jāizgudro.",
      closing: "Mēs nepārdodam produktus. Mēs nesam Latvijas gabalus pasaulē.",
      values: {
        slowLiving: { eyebrow: "Lēna Dzīve", title: "Bez steigas, bez trokšņa.", body: "Katrs gabals ir darināts ar rokām, nelielā skaitā. Mums nekad nebūs zibatlaižu pārdošanas." },
        craft: { eyebrow: "Latvijas Amatniecība", title: "Darināts tur, kur vienmēr ticis darināts.", body: "Mēs strādājam ar aušanas kooperatīviem, dzintara ģimenēm un kokgriezējiem Rīgā, Kurzemē un Vidzemē." },
        amber: { eyebrow: "Dzintars no Jūras", title: "Četrdesmit četri miljoni gadu.", body: "Mūsu dzintars tiek vākts pēc ziemas vētrām gar Jūrmalas baltajām smilšu kāpām. Neapstrādāts. Godīgs." },
        linen: { eyebrow: "Lins no Laukiem", title: "No liniem līdz audumam 200 km.", body: "No sēklas līdz šuvei mūsu lins nekad neatstāj Baltijas reģionu. Audzēts, mērcēts, vērpts, šūts — viss Latvijā." },
      },
      ctaTitle: "Paņemiet gabalu līdzi.",
      ctaBtn: "Izpētīt veikalu",
    },

    latvia: {
      eyebrow: "Zeme aiz BalticRoots",
      heroTitle: "Latvija.",
      heroBody: "Baltijas jūras austrumu krasts. Priežu meži, baltā smiltis, dzintars un lins.",
    },

    journal: {
      eyebrow: "Žurnāls",
      heroTitle: "Vēstules no krasta.",
      heroBody: "Īsi stāsti par dzintaru, linu, latviešu gadalaikiem un cilvēkiem, kas joprojām strādā ar rokām.",
      readStory: "Lasīt stāstu →",
      backToJournal: "← Atpakaļ uz žurnālu",
      eyebrowLabel: "Žurnāls",
    },

    contact: {
      eyebrow: "Kontakti",
      title: "Rakstiet mums.\nKlusi.",
      body: "Mēs atbildam uz katru ziņu divu darba dienu laikā. Mēs atrodamies Rīgā, Latvijā, un sūtām visā pasaulē.",
      emailLabel: "E-pasts",
      studioLabel: "Studija",
      studioValue: "Vecrīga, Rīga, Latvija",
      nameLabel: "Jūsu vārds",
      emailFieldLabel: "E-pasts",
      messageLabel: "Ziņojums",
      submit: "Nosūtīt ziņojumu",
      sending: "Sūta…",
      success: "Paldies. Mēs atbildēsim divu darba dienu laikā.",
      error: "Nevarēja nosūtīt jūsu ziņojumu. Lūdzu, mēģiniet vēlreiz.",
    },

    faq: {
      eyebrow: "Biežāk Uzdotie Jautājumi",
      title: "Jautājumi, atbildēti.",
      items: [
        { q: "Cik ilgi aizņems mans pasūtījums?", a: "Mēs sūtām no Rīgas, Latvijas. ES iekšienē: 3–6 darba dienas. Lielbritānijā un Šveicē: 4–8 darba dienas. Visā pasaulē: 7–14 darba dienas. Bezmaksas piegāde pasūtījumiem virs €75." },
        { q: "Vai dzintars ir īsts Baltijas dzintars?", a: "Jā. Katrs gabals ir īsts Baltijas dzintars, savākts vai iegūts Latvijā. Mēs nekad nepārdodam presētu dzintaru vai kopālu." },
        { q: "Kā rūpēties par manu linu?", a: "Lins ar laiku kļūst skaisti maigs. Mazgājiet vēsā ūdenī (30°C) saudzīgā režīmā, žāvējiet gaisā, gludināt nedaudz mitru." },
        { q: "Vai jūs sūtāt visā pasaulē?", a: "Jā. Mēs sūtām uz ES, Lielbritāniju, ASV, Kanādu, Austrāliju, Japānu un lielāko daļu citu valstu." },
        { q: "Kāda ir jūsu atgriešanas politika?", a: "Atgriešana pieņemta 30 dienu laikā pēc piegādes, nēsāta un oriģinālajā iepakojumā. Rakstiet uz hello@balticroots.store pirms atgriešanas." },
        { q: "Kā atrast savu lina izmēru?", a: "Mūsu lins ir apzināti griezts brīvs. Vairums gabalu der par vienu izmēru lielāki nekā standarta Eiropas izmērs." },
        { q: "Kādus maksājumus jūs pieņemat?", a: "Visas galvenās kartes, Apple Pay, Google Pay, Klarna un bankas pārskaitījums ES pasūtījumiem." },
      ],
    },

    shipping: {
      eyebrow: "Piegāde un Atgriešana",
      title: "No Rīgas, uz jums.",
      sections: [
        { title: "Uz kurieni mēs sūtām", body: "Mēs sūtām no Rīgas, Latvijas, uz Eiropas Savienību, Lielbritāniju, ASV, Kanādu, Austrāliju, Japānu un lielāko daļu citu valstu." },
        { title: "Piegādes laiki", body: "Eiropas Savienība: 3–6 darba dienas. Lielbritānija un Šveice: 4–8 darba dienas. Visā pasaulē: 7–14 darba dienas." },
        { title: "Iepakojums", body: "Katrs gabals ir ietīts nekrāsotā linā un sasiets ar dabīgu pavedienu. Bez plastmasas. Bez putas. Bez trokšņa." },
        { title: "Atgriešana", body: "Atgriešana pieņemta 30 dienu laikā pēc piegādes, nēsāta un oriģinālajā iepakojumā." },
        { title: "Muita un nodokļi", body: "ES pasūtījumi ietver PVN. Pasūtījumi ārpus ES var tikt aplikti ar vietējiem importa nodokļiem." },
        { title: "Pazudis pastā", body: "Ja jūsu pasūtījums nav pienācis paredzētajā laika logā, rakstiet uz hello@balticroots.store." },
      ],
    },

    privacy: {
      eyebrow: "Privātums",
      title: "Klusi apstrādāts.",
      updated: "Pēdējoreiz atjaunināts: Februāris 2026",
      sections: [
        { title: "Ko mēs vācam", body: "Kad jūs veicat pasūtījumu vai pierakstāties mūsu žurnālam, mēs vācam jūsu vārdu, e-pastu, piegādes adresi un nopirktos priekšmetus." },
        { title: "Kāpēc mēs to vācam", body: "Lai nosūtītu jums jūsu pasūtījumu. Lai atbildētu uz jūsu jautājumiem. Lai nosūtītu jums dažas reizes gadā vēstuli no krasta." },
        { title: "Ar ko mēs to kopīgojam", body: "Mūsu maksājumu apstrādātājs, piegādes pakalpojumi un biļetenu sistēma. Neviens cits." },
        { title: "VDAR", body: "Latvija ir Eiropas Savienībā. Jums ir tiesības prasīt, ko mēs glabājam par jums, to labot, dzēst." },
        { title: "Sīkfaili", body: "Mēs izmantojam tikai būtiskus sīkfailus — tos, kurus grozs pieprasa. Bez izsekošanas. Bez reklāmas pikseļiem." },
      ],
    },
  },
};

export default translations;
